/**
 * MD-2026: Mother's Day bundle page controller.
 * Handles scent selection, step-1/step-2 transitions, cart adds, sidebar updates.
 * DELETE WITH CAMPAIGN CLEANUP.
 */
(function() {
  'use strict';

  const MONEY_FORMAT = '${{amount}}';
  const BUNDLE_DISCOUNT_PERCENT = 22;
  const SCRUNCHIE_PERCENT = 100;

  // Locked bundle items (always added alongside whichever scent is chosen).
  const LOCKED_ITEMS = [
    { id: 44199819804851, quantity: 2, role: 'bodywash' },  // Himalayan Salt × 2
    { id: 44944169926835, quantity: 1, role: 'brush' },     // Bamboo Brush
    { id: 45431635247283, quantity: 1, role: 'scrunchie' }, // Green Scrunchie (free)
  ];

  class MothersDayBundle {
    constructor() {
      this.state = {
        currentStep: 1,
        selectedScentVariantId: null,
        selectedScentPrice: 0,
        accessorySelections: new Map(), // variantId (string) → { price, title }
        isSubmitting: false,
      };

      this.init();
    }

    init() {
      this.cacheDOM();
      if (!this.sidebar || !this.mainSection || !this.accessoriesSection) {
        console.warn('[MD-2026] missing required DOM; page init aborted');
        return;
      }
      this.bindEvents();
      this.updateSidebar();
    }

    cacheDOM() {
      this.mainSection        = document.querySelector('[data-step="1"]');
      this.accessoriesSection = document.querySelector('[data-step="2"]');
      this.sidebar            = document.getElementById('md-sidebar');

      this.scentInputs        = document.querySelectorAll('.md-scent-card__input');
      this.accessoryInputs    = document.querySelectorAll('.md-accessory-card__input');

      this.emptyMsg           = document.getElementById('md-sidebar-empty');
      this.itemsEl            = document.getElementById('md-sidebar-items');
      this.totalsEl           = document.getElementById('md-sidebar-totals');
      this.compareEl          = document.getElementById('md-sidebar-compare');
      this.subtotalEl         = document.getElementById('md-sidebar-subtotal');
      this.savingsEl          = document.getElementById('md-sidebar-savings');
      this.ctaBtn             = document.getElementById('md-sidebar-cta');
      this.ctaText            = document.getElementById('md-sidebar-cta-text');
      this.backBtn            = document.getElementById('md-sidebar-back');
    }

    bindEvents() {
      this.scentInputs.forEach((input) => {
        input.addEventListener('change', () => this.onScentChange(input));
      });
      this.accessoryInputs.forEach((input) => {
        input.addEventListener('change', () => this.onAccessoryChange(input));
      });
      this.ctaBtn.addEventListener('click', () => this.handleCtaClick());
      this.backBtn.addEventListener('click', () => this.transitionToStep1());
    }

    onScentChange(input) {
      const card = input.closest('.md-scent-card');
      this.state.selectedScentVariantId = parseInt(input.value, 10);
      this.state.selectedScentPrice = parseInt(card?.dataset.price, 10) || 0;
      this.updateSidebar();
    }

    onAccessoryChange(input) {
      const card = input.closest('.md-accessory-card');
      const id = input.value;
      if (input.checked) {
        this.state.accessorySelections.set(id, {
          price: parseInt(card?.dataset.price, 10) || 0,
          title: card?.querySelector('.md-accessory-card__title')?.textContent?.trim() || '',
        });
      } else {
        this.state.accessorySelections.delete(id);
      }
      this.updateSidebar();
    }

    /* ========== Sidebar rendering ========== */
    updateSidebar() {
      const ready = !!this.state.selectedScentVariantId;

      if (!ready) {
        this.emptyMsg.classList.remove('hidden');
        this.itemsEl.classList.add('hidden');
        this.totalsEl.classList.add('hidden');
        this.ctaBtn.disabled = true;
        this.ctaText.textContent = 'Select a scent to continue';
        return;
      }

      // Compute prices — mirror Shopify's per-unit rounding so the displayed total matches checkout.
      const locked = this.readLockedPrices();
      const scUnit         = this.state.selectedScentPrice;
      const bodyWashUnit   = locked.bodywash  || 0;
      const brushUnit      = locked.brush     || 0;
      const scrunchieUnit  = locked.scrunchie || 0;

      const scPrice        = scUnit;
      const bodyWashFull   = bodyWashUnit * 2;
      const brushFull      = brushUnit;
      const scrunchieFull  = scrunchieUnit;

      const scDiscounted        = Math.round(scUnit        * (100 - BUNDLE_DISCOUNT_PERCENT) / 100);
      const bodyWashDiscounted  = Math.round(bodyWashUnit  * (100 - BUNDLE_DISCOUNT_PERCENT) / 100) * 2;
      const brushDiscounted     = Math.round(brushUnit     * (100 - BUNDLE_DISCOUNT_PERCENT) / 100);
      const scrunchieDiscounted = Math.round(scrunchieUnit * (100 - SCRUNCHIE_PERCENT) / 100);

      const accessoryTotal = Array.from(this.state.accessorySelections.values())
        .reduce((sum, a) => sum + a.price, 0);

      const fullBundle  = scPrice + bodyWashFull + brushFull + scrunchieFull;
      const discBundle  = scDiscounted + bodyWashDiscounted + brushDiscounted + scrunchieDiscounted;
      const fullTotal   = fullBundle + accessoryTotal;
      const finalTotal  = discBundle + accessoryTotal;
      const saved       = fullBundle - discBundle;

      // Build items list. S&C row shows bar type + scent name on 2 lines.
      const scentInfo = this.selectedScentInfo();
      const items = [
        {
          label: scentInfo.barType || 'Shampoo & Conditioner',
          sublabel: scentInfo.scent,
          price: scDiscounted,
          wasPrice: scPrice,
          showStrikethrough: true,
        },
        { label: '2× Himalayan Salt Body Wash Bars', price: bodyWashDiscounted, wasPrice: bodyWashFull, showStrikethrough: true },
        { label: '1× Bamboo Brush', price: brushDiscounted, wasPrice: brushFull, showStrikethrough: true },
        { label: '1× Green Scrunchie', price: 0, wasPrice: scrunchieFull, free: true },
      ];

      if (this.state.accessorySelections.size > 0) {
        items.push({ separator: true, label: 'Added accessories' });
        for (const acc of this.state.accessorySelections.values()) {
          items.push({ label: acc.title, price: acc.price, wasPrice: acc.price });
        }
      }

      this.itemsEl.innerHTML = items.map(this.renderItemRow.bind(this)).join('');

      this.emptyMsg.classList.add('hidden');
      this.itemsEl.classList.remove('hidden');
      this.totalsEl.classList.remove('hidden');

      this.compareEl.textContent  = this.formatMoney(fullTotal);
      this.subtotalEl.textContent = this.formatMoney(finalTotal);

      const percent = fullBundle > 0 ? Math.round((saved / fullBundle) * 100) : 0;
      this.savingsEl.textContent = `${this.formatMoney(saved)} (${percent}%)`;

      this.ctaBtn.disabled = false;
      if (this.state.currentStep === 1) {
        this.ctaText.textContent = `Continue — ${this.formatMoney(finalTotal)}`;
      } else {
        this.ctaText.textContent = `Checkout — ${this.formatMoney(finalTotal)}`;
      }
    }

    renderItemRow(item) {
      if (item.separator) {
        return `<div class="md-sidebar__items-section">${item.label}</div>`;
      }
      let priceHtml;
      if (item.free) {
        priceHtml = `<span class="md-sidebar__free"><s>${this.formatMoney(item.wasPrice)}</s> FREE</span>`;
      } else if (item.showStrikethrough && item.wasPrice > item.price) {
        priceHtml = `<span class="md-sidebar__price-group"><s class="md-sidebar__compare">${this.formatMoney(item.wasPrice)}</s> <span>${this.formatMoney(item.price)}</span></span>`;
      } else {
        priceHtml = `<span>${this.formatMoney(item.price)}</span>`;
      }
      const labelHtml = item.sublabel
        ? `<span class="md-sidebar__item-label"><span class="md-sidebar__item-primary">${item.label}</span><span class="md-sidebar__item-sub">${item.sublabel}</span></span>`
        : `<span class="md-sidebar__item-label"><span class="md-sidebar__item-primary">${item.label}</span></span>`;
      return `<div class="md-sidebar__item">${labelHtml}${priceHtml}</div>`;
    }

    selectedScentInfo() {
      const radio = Array.from(this.scentInputs).find(i => i.checked);
      const card  = radio?.closest('.md-scent-card');
      const barType = card?.querySelector('.md-scent-card__label')?.textContent?.trim() || '';
      const scent   = card?.querySelector('.md-scent-card__desc')?.textContent?.trim() || '';
      return { barType, scent };
    }

    readLockedPrices() {
      const prices = {};
      document.querySelectorAll('.md-locked-item').forEach((el) => {
        const variantId = parseInt(el.dataset.variantId, 10);
        const price = parseInt(el.dataset.price, 10) || 0;
        if (variantId === 44199819804851) prices.bodywash = price;
        if (variantId === 44944169926835) prices.brush = price;
        if (variantId === 45431635247283) prices.scrunchie = price;
      });
      return prices;
    }

    /* ========== CTA handler ========== */
    async handleCtaClick() {
      if (this.state.isSubmitting) return;
      if (!this.state.selectedScentVariantId) return;

      if (this.state.currentStep === 1) {
        await this.addBundleToCart();
      } else {
        await this.handleCheckout();
      }
    }

    async addBundleToCart() {
      this.state.isSubmitting = true;
      this.ctaBtn.disabled = true;

      try {
        const items = [
          { id: this.state.selectedScentVariantId, quantity: 1 },
          ...LOCKED_ITEMS.map(({ id, quantity }) => ({ id, quantity })),
        ];
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.description || 'Cart add failed');
        }
        this.transitionToStep2();
      } catch (err) {
        console.error('[MD-2026] addBundleToCart failed', err);
        alert("Something went wrong adding the bundle to cart. Please try again.");
      } finally {
        this.state.isSubmitting = false;
      }
    }

    async handleCheckout() {
      this.state.isSubmitting = true;
      this.ctaBtn.disabled = true;

      try {
        if (this.state.accessorySelections.size > 0) {
          const items = Array.from(this.state.accessorySelections.keys()).map((id) => ({
            id: parseInt(id, 10),
            quantity: 1,
          }));
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items }),
          });
          if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.description || 'Accessory add failed');
          }
        }
        window.location.href = '/checkout';
      } catch (err) {
        console.error('[MD-2026] checkout failed', err);
        alert("Something went wrong proceeding to checkout. Please try again.");
        this.state.isSubmitting = false;
      }
    }

    /* ========== Step transitions ========== */
    transitionToStep2() {
      this.state.currentStep = 2;
      this.mainSection.classList.add('md-step-hidden');
      this.accessoriesSection.classList.remove('md-step-hidden');
      this.backBtn.classList.remove('hidden');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      this.updateSidebar();
    }

    transitionToStep1() {
      this.state.currentStep = 1;
      this.accessoriesSection.classList.add('md-step-hidden');
      this.mainSection.classList.remove('md-step-hidden');
      this.backBtn.classList.add('hidden');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      this.updateSidebar();
    }

    /* ========== Utilities ========== */
    formatMoney(cents) {
      const price = (cents / 100).toFixed(2);
      return `$${price}`;
    }
  }

  // Initialize when page is ready
  function init() {
    if (document.getElementById('md-sidebar')) {
      window.MothersDayBundleInstance = new MothersDayBundle();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
